import { QrPlaceholder } from "./QrPlaceholder.tsx";
import { IDENTITY_REQUIREMENTS } from "./signup.constants.ts";

type Props = {
  busy: boolean;
  error: string | null;
  onBack: () => void;
  onSkip: () => void;
};

/**
 * Identity verification, framed as a phone scan rather than a desktop upload.
 *
 * Document capture on a laptop webcam is the worst version of this: the images
 * are poor enough to fail automated checks, and passports have to be held at
 * an angle nobody manages first time. The provider's own flow will be a phone
 * camera, so the desktop screen's job is to get the applicant onto their phone.
 *
 * Nothing here is wired up yet — no provider, no session, no scannable code.
 * The panel says so plainly and the step stays skippable, so signup completes
 * without it.
 *
 * THE PROVIDER'S NAME AND LOGO ARE GONE, which is OND-75. This panel carried a
 * "Powered by Sumsub" mark, the line "Trusted KYC & AML verification", and an
 * "ISO 27001" badge — over a single button reading "Skip for now", with the skip
 * hard-coded on. Three claims about a third party ONDO has no integration with,
 * and a certification that belongs to somebody else, above a step that does
 * nothing. Whoever connects a provider puts its name back, in the same commit as
 * the session that makes it true.
 *
 * What stays is the part that is honest and useful: the phone-scan framing, which
 * is what the flow will be, and the plain statement that it is not live.
 */
export function IdentityPanel({ busy, error, onBack, onSkip }: Props) {
  return (
    <div className="signup__identity">
      <div className="signup__provider">
        <div className="signup__scan">
          <QrPlaceholder />
          <div className="signup__scan-copy">
            <p className="signup__scan-title">Scan with your phone</p>
            <p className="signup__scan-body">
              Identity documents are photographed with your phone camera, not
              uploaded here — the images a laptop webcam produces are rarely good
              enough to pass an automated check.
            </p>
            <p className="signup__scan-pending">
              The scan is not live yet, so this code does nothing. Skip for now
              and we will ask you to verify before your first transfer.
            </p>
          </div>
        </div>

        <ul className="signup__requirements">
          {IDENTITY_REQUIREMENTS.map((requirement) => (
            <li className="signup__requirement" key={requirement.title}>
              <span className="signup__requirement-icon" aria-hidden="true">
                {requirement.icon}
              </span>
              <span>
                <span className="signup__requirement-title">{requirement.title}</span>
                <span className="signup__requirement-detail">{requirement.detail}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/*
        NO CLAIM ABOUT ENCRYPTION OR GDPR PROCESSING, which this also carried.
        Both were true of nothing: there is no document to encrypt and no
        processing to be compliant about, because the step does not run. The
        second sentence survives because it is the one thing that IS true today —
        ONDO holds no identity documents at all.
      */}
      <p className="signup__fineprint">
        ONDO does not hold copies of your identity documents.
      </p>

      {error && (
        <p className="signup__error" role="alert">
          {error}
        </p>
      )}

      <div className="signup__actions">
        <button
          type="button"
          className="signup__back"
          disabled={busy}
          onClick={onBack}
        >
          ← Back
        </button>
        <button
          type="button"
          className="signup__submit"
          disabled={busy}
          onClick={onSkip}
        >
          {busy ? "Sending code…" : "Skip for now →"}
        </button>
      </div>
    </div>
  );
}
